import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import {
  CalendarDaysIcon,
  SparklesIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  TrophyIcon
} from '@heroicons/react/24/outline';

const RESOURCE_TYPES = [
  { value: '', label: 'Any resource type' },
  { value: 'field', label: 'Field' },
  { value: 'batting_cage', label: 'Batting Cage' },
  { value: 'bullpen', label: 'Bullpen' },
  { value: 'indoor_turf', label: 'Indoor Turf' },
  { value: 'gym', label: 'Gym' },
  { value: 'other', label: 'Other' }
];

const DAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const TABS = [
  { key: 'master', label: 'Master Schedule' },
  { key: 'practices', label: 'Practices' },
  { key: 'season', label: 'Regular Season' },
  { key: 'standings', label: 'Standings' },
  { key: 'postseason', label: 'Postseason' }
];

function poolKeyOf(team) {
  return `${team.age_group_id ?? 'none'}::${team.division ?? 'none'}`;
}

function groupTeamsIntoPools(teams) {
  const pools = new Map();
  (teams || []).forEach(team => {
    const key = poolKeyOf(team);
    if (!pools.has(key)) {
      pools.set(key, { key, age_group_id: team.age_group_id, division: team.division, ageGroupLabel: team.ageGroup, teams: [] });
    }
    pools.get(key).teams.push(team);
  });
  return Array.from(pools.values());
}

function poolLabel(pool) {
  const ageLabel = pool.ageGroupLabel || 'Unassigned age group';
  return pool.division ? `${ageLabel} — ${pool.division}` : ageLabel;
}

// "Field 1 — Riverside Sports Complex" so it's clear which park a booked resource is in.
function resourceLabel(resource) {
  if (!resource) return '';
  return resource.Facility?.name ? `${resource.name} — ${resource.Facility.name}` : resource.name;
}

// Small inline score-entry control shared by the Regular Season and Postseason tabs.
const ScoreEntry = ({ event, onSave }) => {
  const [homeScore, setHomeScore] = useState(event.homeScore ?? '');
  const [awayScore, setAwayScore] = useState(event.awayScore ?? '');
  const alreadyScored = event.homeScore !== null && event.homeScore !== undefined;

  return (
    <div className="flex items-center gap-2">
      <input type="number" min="0" value={homeScore} onChange={(e) => setHomeScore(e.target.value)}
        className="w-14 px-2 py-1 text-sm border border-gray-300 rounded" placeholder="H" />
      <span className="text-xs text-gray-400">-</span>
      <input type="number" min="0" value={awayScore} onChange={(e) => setAwayScore(e.target.value)}
        className="w-14 px-2 py-1 text-sm border border-gray-300 rounded" placeholder="A" />
      <button
        onClick={() => onSave(homeScore, awayScore)}
        disabled={homeScore === '' || awayScore === ''}
        className="px-2 py-1 text-xs bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-40"
      >
        {alreadyScored ? 'Update' : 'Save'}
      </button>
    </div>
  );
};

const ScheduleGenerator = () => {
  const { user } = useAuth();
  const token = user?.token || localStorage.getItem('token');
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  const [activeTab, setActiveTab] = useState('master');

  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [programs, setPrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState('');
  const [programDetail, setProgramDetail] = useState(null); // full Program + Teams (with division)

  const [masterProgramIds, setMasterProgramIds] = useState([]);
  const [masterIncludePractices, setMasterIncludePractices] = useState(true);
  const [masterIncludeGames, setMasterIncludeGames] = useState(true);
  const [generatingMaster, setGeneratingMaster] = useState(false);
  const [masterLastRun, setMasterLastRun] = useState(null);
  const [masterSchedule, setMasterSchedule] = useState(null);
  const [masterScheduleLoading, setMasterScheduleLoading] = useState(false);

  const [preferences, setPreferences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const [lastRun, setLastRun] = useState(null);
  const [runDetail, setRunDetail] = useState(null);

  const [seasonLastRun, setSeasonLastRun] = useState(null);
  const [seasonRunDetail, setSeasonRunDetail] = useState(null);
  const [generatingSeason, setGeneratingSeason] = useState(false);

  const [selectedPoolKey, setSelectedPoolKey] = useState('');
  const [standings, setStandings] = useState([]);
  const [standingsLoading, setStandingsLoading] = useState(false);

  const [tournament, setTournament] = useState(null); // { tournament, matches, seeds } or null
  const [tournamentLoading, setTournamentLoading] = useState(false);
  const [generatingTournament, setGeneratingTournament] = useState(false);

  const selectedProgramObj = programs.find(p => p.id.toString() === selectedProgram) || null;
  const pools = groupTeamsIntoPools(programDetail?.Teams);
  const selectedPool = pools.find(p => p.key === selectedPoolKey) || pools[0] || null;

  useEffect(() => {
    if (!token) return;
    fetchOrganizations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchOrganizations = async () => {
    try {
      const response = await axios.get('/api/organizations/my-organizations', { headers: authHeaders });
      const orgs = Array.isArray(response.data) ? response.data : [];
      const transformed = orgs.map(orgUser => (orgUser.Organization
        ? { id: orgUser.Organization.id, name: orgUser.Organization.name }
        : orgUser));
      setOrganizations(transformed);
      if (transformed.length > 0 && !selectedOrg) {
        setSelectedOrg(transformed[0].id.toString());
      }
    } catch (error) {
      toast.error('Failed to load organizations');
    }
  };

  const fetchPrograms = useCallback(async () => {
    if (!token || !selectedOrg) return;
    try {
      const response = await axios.get(`/api/programs?organization_id=${selectedOrg}&active=true`, { headers: authHeaders });
      const rows = Array.isArray(response.data) ? response.data : [];
      setPrograms(rows);
      setMasterProgramIds(previous => {
        const available = new Set(rows.map(program => program.id));
        const retained = previous.filter(id => available.has(id));
        return retained.length ? retained : rows.map(program => program.id);
      });
      if (rows.length > 0) {
        setSelectedProgram(prev => (rows.some(p => p.id.toString() === prev) ? prev : rows[0].id.toString()));
      } else {
        setSelectedProgram('');
      }
    } catch (error) {
      toast.error('Failed to load programs');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedOrg]);

  const fetchProgramDetail = useCallback(async () => {
    if (!token || !selectedProgram) { setProgramDetail(null); return; }
    try {
      const response = await axios.get(`/api/programs/${selectedProgram}`, { headers: authHeaders });
      setProgramDetail(response.data);
    } catch (error) {
      setProgramDetail(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedProgram]);

  const fetchPreferences = useCallback(async () => {
    if (!token || !selectedProgram) { setPreferences([]); return; }
    try {
      setLoading(true);
      const response = await axios.get(`/api/scheduling/programs/${selectedProgram}/team-preferences`, { headers: authHeaders });
      setPreferences(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load team preferences');
      setPreferences([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedProgram]);

  useEffect(() => {
    fetchPrograms();
  }, [fetchPrograms]);

  useEffect(() => {
    setMasterLastRun(null);
    setMasterSchedule(null);
  }, [selectedOrg]);

  useEffect(() => {
    fetchProgramDetail();
    fetchPreferences();
    setLastRun(null);
    setRunDetail(null);
    setSeasonLastRun(null);
    setSeasonRunDetail(null);
    setTournament(null);
    setSelectedPoolKey('');
  }, [fetchProgramDetail, fetchPreferences]);

  useEffect(() => {
    if (!selectedPoolKey && pools.length > 0) {
      setSelectedPoolKey(pools[0].key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pools.length]);

  // ---- Master schedule ----

  const toggleMasterProgram = (programId) => {
    setMasterProgramIds(previous => previous.includes(programId)
      ? previous.filter(id => id !== programId)
      : [...previous, programId]);
  };

  const fetchMasterSchedule = useCallback(async () => {
    if (!token || !selectedOrg || masterProgramIds.length === 0) {
      setMasterSchedule(null);
      return;
    }
    try {
      setMasterScheduleLoading(true);
      const programIds = [...masterProgramIds].sort((a, b) => a - b).join(',');
      const response = await axios.get(`/api/scheduling/organizations/${selectedOrg}/master-schedule?program_ids=${programIds}`, { headers: authHeaders });
      setMasterSchedule(response.data);
    } catch (error) {
      setMasterSchedule(null);
    } finally {
      setMasterScheduleLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedOrg, masterProgramIds]);

  useEffect(() => {
    if (activeTab === 'master') fetchMasterSchedule();
  }, [activeTab, fetchMasterSchedule]);

  const generateMasterSchedule = async () => {
    if (!selectedOrg || masterProgramIds.length === 0) return;
    if (!window.confirm(`Generate one shared schedule for ${masterProgramIds.length} program${masterProgramIds.length === 1 ? '' : 's'}? Existing events will be preserved as hard constraints.`)) return;
    try {
      setGeneratingMaster(true);
      const response = await axios.post(`/api/scheduling/organizations/${selectedOrg}/generate-master`, {
        program_ids: masterProgramIds,
        include_practices: masterIncludePractices,
        include_regular_season: masterIncludeGames
      }, { headers: authHeaders });
      setMasterLastRun(response.data);
      await fetchMasterSchedule();
      toast.success(`Generated ${response.data.generated_event_count} events across ${masterProgramIds.length} program${masterProgramIds.length === 1 ? '' : 's'}`);
      if (response.data.unmet?.length) {
        toast.error(`${response.data.unmet.length} scheduling request(s) could not be met`);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to generate master schedule');
    } finally {
      setGeneratingMaster(false);
    }
  };

  const rollbackMasterSchedule = async () => {
    if (!masterLastRun?.run_id) return;
    if (!window.confirm('Roll back this entire master schedule? Manually edited events will be kept.')) return;
    try {
      const response = await axios.post(`/api/scheduling/runs/${masterLastRun.run_id}/rollback`, {}, { headers: authHeaders });
      toast.success(`Rolled back: ${response.data.deleted} event(s) removed`);
      setMasterLastRun(null);
      await fetchMasterSchedule();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to roll back master schedule');
    }
  };

  // ---- Practices ----

  const updatePreferenceField = (teamId, field, value) => {
    setPreferences(prev => prev.map(p => p.team_id === teamId ? { ...p, [field]: value } : p));
  };

  const toggleDay = (teamId, dayIndex) => {
    setPreferences(prev => prev.map(p => {
      if (p.team_id !== teamId) return p;
      const current = Array.isArray(p.preferred_days) ? p.preferred_days : [];
      const next = current.includes(dayIndex) ? current.filter(d => d !== dayIndex) : [...current, dayIndex];
      return { ...p, preferred_days: next };
    }));
  };

  const savePreference = async (pref) => {
    try {
      await axios.put(`/api/scheduling/programs/${selectedProgram}/team-preferences/${pref.team_id}`, {
        practices_per_week: parseInt(pref.practices_per_week, 10) || 1,
        practice_duration_minutes: parseInt(pref.practice_duration_minutes, 10) || 60,
        preferred_resource_type: pref.preferred_resource_type || null,
        preferred_days: pref.preferred_days?.length ? pref.preferred_days : null,
        notes: pref.notes || ''
      }, { headers: authHeaders });
      toast.success(`Saved preferences for ${pref.team_name}`);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save preferences');
    }
  };

  const generateSchedule = async () => {
    if (!selectedProgram) return;
    if (!window.confirm('Generate practices for this program? This will book available resource slots for every team in this program.')) return;
    try {
      setGenerating(true);
      const response = await axios.post(`/api/scheduling/programs/${selectedProgram}/generate`, {}, { headers: authHeaders });
      setLastRun(response.data);
      toast.success(`Generated ${response.data.generated_event_count} practice${response.data.generated_event_count === 1 ? '' : 's'}`);
      if (response.data.unmet?.length) {
        toast.error(`${response.data.unmet.length} team-week(s) could not be fully scheduled - see details below`);
      }
      await loadRunDetail(response.data.run_id);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to generate schedule');
    } finally {
      setGenerating(false);
    }
  };

  const loadRunDetail = async (runId) => {
    try {
      const response = await axios.get(`/api/scheduling/runs/${runId}`, { headers: authHeaders });
      setRunDetail(response.data);
    } catch (error) {
      console.error('Error loading run detail:', error);
    }
  };

  const rollbackRun = async () => {
    if (!lastRun?.run_id) return;
    if (!window.confirm('Roll back this generated schedule? Practices that were not manually edited will be deleted.')) return;
    try {
      const response = await axios.post(`/api/scheduling/runs/${lastRun.run_id}/rollback`, {}, { headers: authHeaders });
      toast.success(`Rolled back: ${response.data.deleted} practice(s) removed`);
      setLastRun(null);
      setRunDetail(null);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to roll back schedule');
    }
  };

  // ---- Regular season (round robin) ----

  const generateSeasonGames = async () => {
    if (!selectedProgram) return;
    if (!window.confirm('Generate round-robin games for every pool in this program? This will book available field slots.')) return;
    try {
      setGeneratingSeason(true);
      const response = await axios.post(`/api/scheduling/programs/${selectedProgram}/generate-games`, {}, { headers: authHeaders });
      setSeasonLastRun(response.data);
      toast.success(`Generated ${response.data.generated_event_count} game${response.data.generated_event_count === 1 ? '' : 's'}`);
      if (response.data.unmet?.length) {
        toast.error(`${response.data.unmet.length} matchup(s) could not be scheduled - see details below`);
      }
      const runResp = await axios.get(`/api/scheduling/runs/${response.data.run_id}`, { headers: authHeaders });
      setSeasonRunDetail(runResp.data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to generate regular season games');
    } finally {
      setGeneratingSeason(false);
    }
  };

  const rollbackSeasonRun = async () => {
    if (!seasonLastRun?.run_id) return;
    if (!window.confirm('Roll back the generated regular season games?')) return;
    try {
      const response = await axios.post(`/api/scheduling/runs/${seasonLastRun.run_id}/rollback`, {}, { headers: authHeaders });
      toast.success(`Rolled back: ${response.data.deleted} game(s) removed`);
      setSeasonLastRun(null);
      setSeasonRunDetail(null);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to roll back');
    }
  };

  const saveGameScore = async (eventId, homeScore, awayScore, onDone) => {
    try {
      await axios.put(`/api/scheduling/games/${eventId}/score`, {
        home_score: parseInt(homeScore, 10),
        away_score: parseInt(awayScore, 10)
      }, { headers: authHeaders });
      toast.success('Score saved');
      if (onDone) await onDone();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save score');
    }
  };

  // ---- Standings ----

  const fetchStandings = useCallback(async () => {
    if (!selectedProgram || !selectedPool) { setStandings([]); return; }
    try {
      setStandingsLoading(true);
      const params = new URLSearchParams();
      if (selectedPool.age_group_id) params.set('ageGroupId', selectedPool.age_group_id);
      if (selectedPool.division) params.set('division', selectedPool.division);
      const response = await axios.get(`/api/scheduling/programs/${selectedProgram}/standings?${params.toString()}`, { headers: authHeaders });
      setStandings(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setStandings([]);
    } finally {
      setStandingsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProgram, selectedPool?.key]);

  useEffect(() => {
    if (activeTab === 'standings') fetchStandings();
  }, [activeTab, fetchStandings]);

  // ---- Postseason bracket ----

  const fetchTournament = useCallback(async () => {
    if (!selectedProgram || !selectedPool) { setTournament(null); return; }
    try {
      setTournamentLoading(true);
      const params = new URLSearchParams();
      if (selectedPool.age_group_id) params.set('ageGroupId', selectedPool.age_group_id);
      if (selectedPool.division) params.set('division', selectedPool.division);
      const response = await axios.get(`/api/tournaments/programs/${selectedProgram}/tournament?${params.toString()}`, { headers: authHeaders });
      setTournament(response.data);
    } catch (error) {
      setTournament(null);
    } finally {
      setTournamentLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProgram, selectedPool?.key]);

  useEffect(() => {
    if (activeTab === 'postseason') fetchTournament();
  }, [activeTab, fetchTournament]);

  const generateTournament = async () => {
    if (!selectedProgram || !selectedPool) return;
    if (!window.confirm('Generate the postseason bracket for this pool? This seeds teams from current standings and books field slots for every round.')) return;
    try {
      setGeneratingTournament(true);
      await axios.post(`/api/tournaments/programs/${selectedProgram}/tournament`, {
        age_group_id: selectedPool.age_group_id || null,
        division: selectedPool.division || null
      }, { headers: authHeaders });
      toast.success('Bracket generated');
      await fetchTournament();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to generate bracket');
    } finally {
      setGeneratingTournament(false);
    }
  };

  const rollbackTournament = async () => {
    if (!tournament?.tournament?.id) return;
    if (!window.confirm('Roll back this bracket? Games that have not been manually edited will be removed.')) return;
    try {
      const response = await axios.post(`/api/tournaments/${tournament.tournament.id}/rollback`, {}, { headers: authHeaders });
      toast.success(`Rolled back: ${response.data.deleted} game(s) removed`);
      setTournament(null);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to roll back bracket');
    }
  };

  const saveMatchScore = async (matchId, homeScore, awayScore) => {
    try {
      await axios.put(`/api/tournaments/tournament-matches/${matchId}/score`, {
        home_score: parseInt(homeScore, 10),
        away_score: parseInt(awayScore, 10)
      }, { headers: authHeaders });
      toast.success('Score saved');
      await fetchTournament();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save score');
    }
  };

  const renderPoolSelector = () => (
    pools.length === 0 ? null : (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Pool (age group / division)</label>
        <select
          value={selectedPoolKey}
          onChange={(e) => setSelectedPoolKey(e.target.value)}
          className="w-full md:w-96 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          {pools.map(p => <option key={p.key} value={p.key}>{poolLabel(p)} ({p.teams.length} teams)</option>)}
        </select>
      </div>
    )
  );

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <CalendarDaysIcon className="h-8 w-8 text-primary-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Schedule Generator</h1>
          <p className="text-sm text-gray-500">Coordinate shared facilities across programs, from practices through postseason play.</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Organization</label>
          <select
            value={selectedOrg}
            onChange={(e) => setSelectedOrg(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Select an organization</option>
            {organizations.map(org => (
              <option key={org.id} value={org.id}>{org.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Program detail</label>
          <select
            value={selectedProgram}
            onChange={(e) => setSelectedProgram(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Select a program</option>
            {programs.map(p => (
              <option key={p.id} value={p.id}>{p.name}{p.season ? ` (${p.season})` : ''}</option>
            ))}
          </select>
        </div>
      </div>

      {programs.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
          No active programs are available for this organization.
        </div>
      ) : (
        <>
          <div className="border-b border-gray-200 mb-6">
            <nav className="flex gap-6">
              {TABS.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`pb-3 text-sm font-medium border-b-2 ${
                    activeTab === tab.key
                      ? 'border-primary-600 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {activeTab === 'master' && (
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow">
                <div className="px-4 py-3 border-b border-gray-100 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div>
                    <h2 className="font-semibold text-gray-800">Shared Facility Schedule</h2>
                    <p className="text-xs text-gray-500 mt-0.5">Programs are allocated together so one program cannot consume all preferred slots before the others are considered.</p>
                  </div>
                  <button
                    onClick={generateMasterSchedule}
                    disabled={generatingMaster || masterProgramIds.length === 0 || (!masterIncludePractices && !masterIncludeGames)}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                  >
                    <SparklesIcon className="h-5 w-5" /> {generatingMaster ? 'Generating...' : 'Generate Master Schedule'}
                  </button>
                </div>

                <div className="p-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Programs sharing these facilities</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-5">
                    {programs.map(program => (
                      <label key={program.id} className="flex items-start gap-2 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                        <input type="checkbox" checked={masterProgramIds.includes(program.id)}
                          onChange={() => toggleMasterProgram(program.id)} className="mt-0.5" />
                        <span>
                          <span className="block text-sm font-medium text-gray-900">{program.name}</span>
                          <span className="block text-xs text-gray-500">
                            {program.start_date}{program.end_date ? ` to ${program.end_date}` : ''}{program.season ? ` · ${program.season}` : ''}
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>

                  <p className="text-sm font-medium text-gray-700 mb-2">Include</p>
                  <div className="flex flex-wrap gap-5 text-sm text-gray-700">
                    <label className="inline-flex items-center gap-2">
                      <input type="checkbox" checked={masterIncludeGames} onChange={(e) => setMasterIncludeGames(e.target.checked)} />
                      Regular-season games
                    </label>
                    <label className="inline-flex items-center gap-2">
                      <input type="checkbox" checked={masterIncludePractices} onChange={(e) => setMasterIncludePractices(e.target.checked)} />
                      Practices
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 mt-3">Games are placed first because they have fixed opponents and tighter constraints. Practice-only programs use their full program date range; programs with a regular season use their preseason practice window.</p>
                </div>
              </div>

              {masterLastRun && (
                <div className="bg-white rounded-lg shadow">
                  <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                    <h2 className="font-semibold text-gray-800">Last master run: {masterLastRun.generated_event_count} events</h2>
                    <button onClick={rollbackMasterSchedule}
                      className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-red-50 text-red-700 rounded hover:bg-red-100">
                      <ArrowPathIcon className="h-4 w-4" /> Roll back all
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-4 border-b border-gray-100">
                    {(masterLastRun.by_program || []).map(summary => (
                      <div key={summary.program_id} className="border border-gray-200 rounded-lg p-3">
                        <p className="text-sm font-medium text-gray-900">{summary.program_name}</p>
                        <p className="text-xs text-gray-500 mt-1">{summary.game_count} games · {summary.practice_count} practices</p>
                        {summary.unmet_count > 0 && <p className="text-xs text-amber-700 mt-1">{summary.unmet_count} unmet request(s)</p>}
                      </div>
                    ))}
                  </div>

                  {masterLastRun.unmet?.length > 0 && (
                    <div className="px-4 py-3 bg-amber-50 border-b border-amber-100">
                      <p className="text-sm font-medium text-amber-800 inline-flex items-center gap-1 mb-1">
                        <ExclamationTriangleIcon className="h-4 w-4" /> Capacity conflicts to resolve
                      </p>
                      <ul className="text-xs text-amber-700 list-disc list-inside space-y-0.5 max-h-36 overflow-y-auto">
                        {masterLastRun.unmet.map((item, index) => (
                          <li key={index}>
                            {item.program_name}: {item.kind === 'game'
                              ? `${item.home_team_name} vs ${item.away_team_name}`
                              : `${item.team_name}, week of ${item.week_start} (needed ${item.needed}, found ${item.found})`}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                </div>
              )}

              <div className="bg-white rounded-lg shadow">
                <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                  <div>
                    <h2 className="font-semibold text-gray-800">Current Master Calendar</h2>
                    {masterSchedule?.start_date && (
                      <p className="text-xs text-gray-500 mt-0.5">{masterSchedule.start_date} to {masterSchedule.end_date}</p>
                    )}
                  </div>
                  <button onClick={fetchMasterSchedule} disabled={masterScheduleLoading || masterProgramIds.length === 0}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50">
                    <ArrowPathIcon className={`h-4 w-4 ${masterScheduleLoading ? 'animate-spin' : ''}`} /> Refresh
                  </button>
                </div>
                {masterScheduleLoading ? (
                  <div className="p-8 text-center text-sm text-gray-500">Loading master schedule...</div>
                ) : masterSchedule?.events?.length > 0 ? (
                  <div className="max-h-[32rem] overflow-y-auto divide-y divide-gray-100">
                    {masterSchedule.events.map(event => (
                      <div key={event.id} className="px-4 py-2 text-sm flex flex-col md:flex-row md:items-center md:justify-between gap-1">
                        <span>
                          <span className="text-xs font-medium text-primary-700 mr-2">{event.Program?.name}</span>
                          <span className="font-medium text-gray-900">{event.title}</span>{' '}
                          <span className="text-gray-500">{event.date} {event.time?.slice(0, 5)}{event.endTime ? `-${event.endTime.slice(0, 5)}` : ''}</span>
                        </span>
                        <span className="text-xs text-gray-500">{resourceLabel(event.FacilityResource)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center text-sm text-gray-500">No scheduled events for the selected programs.</div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'practices' && (
            loading ? (
              <div className="text-center py-12 text-gray-500">Loading teams...</div>
            ) : preferences.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
                This program has no teams assigned yet. Add teams to it from the Programs page first.
              </div>
            ) : (
              <>
                <div className="bg-white rounded-lg shadow mb-6">
                  <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                    <h2 className="font-semibold text-gray-800">Team Practice Preferences</h2>
                    <button
                      onClick={generateSchedule}
                      disabled={generating}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                    >
                      <SparklesIcon className="h-5 w-5" /> {generating ? 'Generating...' : 'Generate Schedule'}
                    </button>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {preferences.map(pref => (
                      <div key={pref.team_id} className="p-4 grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
                        <div className="md:col-span-1">
                          <span className="text-sm font-medium text-gray-900">{pref.team_name}</span>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Practices / week</label>
                          <input type="number" min="1" max="7" value={pref.practices_per_week}
                            onChange={(e) => updatePreferenceField(pref.team_id, 'practices_per_week', e.target.value)}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Duration (min)</label>
                          <input type="number" min="15" step="15" value={pref.practice_duration_minutes}
                            onChange={(e) => updatePreferenceField(pref.team_id, 'practice_duration_minutes', e.target.value)}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Resource type</label>
                          <select value={pref.preferred_resource_type || ''}
                            onChange={(e) => updatePreferenceField(pref.team_id, 'preferred_resource_type', e.target.value)}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg">
                            {RESOURCE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                          </select>
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-gray-700 mb-1">Preferred days</label>
                          <div className="flex gap-1 flex-wrap">
                            {DAY_LABELS.map((label, i) => (
                              <button
                                key={i}
                                type="button"
                                onClick={() => toggleDay(pref.team_id, i)}
                                className={`px-2 py-1 text-xs rounded border ${
                                  (pref.preferred_days || []).includes(i)
                                    ? 'bg-primary-600 text-white border-primary-600'
                                    : 'bg-white text-gray-600 border-gray-300'
                                }`}
                              >
                                {label}
                              </button>
                            ))}
                            <button
                              onClick={() => savePreference(pref)}
                              className="ml-2 px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                            >
                              Save
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {lastRun && (
                  <div className="bg-white rounded-lg shadow mb-6">
                    <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                      <h2 className="font-semibold text-gray-800">
                        Last run: {lastRun.generated_event_count} practice{lastRun.generated_event_count === 1 ? '' : 's'} generated
                      </h2>
                      <button
                        onClick={rollbackRun}
                        className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-red-50 text-red-700 rounded hover:bg-red-100"
                      >
                        <ArrowPathIcon className="h-4 w-4" /> Roll back
                      </button>
                    </div>

                    {lastRun.unmet?.length > 0 && (
                      <div className="px-4 py-3 bg-amber-50 border-b border-amber-100">
                        <p className="text-sm font-medium text-amber-800 inline-flex items-center gap-1 mb-1">
                          <ExclamationTriangleIcon className="h-4 w-4" /> Some practices could not be scheduled
                        </p>
                        <ul className="text-xs text-amber-700 list-disc list-inside space-y-0.5">
                          {lastRun.unmet.map((u, i) => (
                            <li key={i}>
                              {u.team_name}: needed {u.needed}, found {u.found} (week of {u.week_start})
                            </li>
                          ))}
                        </ul>
                        <p className="text-xs text-amber-600 mt-1">Add more resource availability or adjust practice preferences and regenerate.</p>
                      </div>
                    )}

                    {runDetail?.events?.length > 0 && (
                      <div className="max-h-80 overflow-y-auto divide-y divide-gray-100">
                        {runDetail.events.map(ev => (
                          <div key={ev.id} className="px-4 py-2 text-sm flex items-center justify-between">
                            <span>
                              <span className="font-medium text-gray-900">{ev.Team?.name}</span>{' '}
                              <span className="text-gray-500">{ev.date} {ev.time?.slice(0, 5)}-{ev.endTime?.slice(0, 5)}</span>
                            </span>
                            <span className="text-xs text-gray-500">{resourceLabel(ev.FacilityResource)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )
          )}

          {activeTab === 'season' && (
            <div className="bg-white rounded-lg shadow mb-6">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <div>
                  <h2 className="font-semibold text-gray-800">Regular Season (Round Robin)</h2>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {selectedProgramObj?.regular_season_start_date && selectedProgramObj?.regular_season_end_date
                      ? `${selectedProgramObj.regular_season_start_date} to ${selectedProgramObj.regular_season_end_date} · each team plays every other team in its pool ${selectedProgramObj.round_robin_times_through}x`
                      : 'Set regular season start/end dates on the Programs page first.'}
                  </p>
                </div>
                <button
                  onClick={generateSeasonGames}
                  disabled={generatingSeason || !selectedProgramObj?.regular_season_start_date || !selectedProgramObj?.regular_season_end_date}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  <SparklesIcon className="h-5 w-5" /> {generatingSeason ? 'Generating...' : 'Generate Games'}
                </button>
              </div>

              {pools.length > 0 && (
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-xs font-medium text-gray-600 mb-1">Pools in this program:</p>
                  <div className="flex flex-wrap gap-2">
                    {pools.map(p => (
                      <span key={p.key} className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded">
                        {poolLabel(p)} ({p.teams.length})
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {seasonLastRun && (
                <>
                  <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-gray-800">
                      Last run: {seasonLastRun.generated_event_count} game{seasonLastRun.generated_event_count === 1 ? '' : 's'} generated
                    </h3>
                    <button
                      onClick={rollbackSeasonRun}
                      className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-red-50 text-red-700 rounded hover:bg-red-100"
                    >
                      <ArrowPathIcon className="h-4 w-4" /> Roll back
                    </button>
                  </div>

                  {seasonLastRun.unmet?.length > 0 && (
                    <div className="px-4 py-3 bg-amber-50 border-b border-amber-100">
                      <p className="text-sm font-medium text-amber-800 inline-flex items-center gap-1 mb-1">
                        <ExclamationTriangleIcon className="h-4 w-4" /> Some games could not be scheduled
                      </p>
                      <ul className="text-xs text-amber-700 list-disc list-inside space-y-0.5">
                        {seasonLastRun.unmet.map((u, i) => (
                          <li key={i}>{u.home_team_name} vs {u.away_team_name}: {u.reason}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {seasonRunDetail?.events?.length > 0 && (
                    <div className="max-h-96 overflow-y-auto divide-y divide-gray-100">
                      {seasonRunDetail.events.map(ev => (
                        <div key={ev.id} className="px-4 py-2 text-sm flex items-center justify-between gap-3">
                          <span className="flex-1">
                            <span className="font-medium text-gray-900">{ev.Team?.name}</span>
                            <span className="text-gray-400"> vs </span>
                            <span className="font-medium text-gray-900">{ev.AwayTeam?.name}</span>{' '}
                            <span className="text-gray-500">{ev.date} {ev.time?.slice(0, 5)}</span>{' '}
                            {ev.FacilityResource?.name && (
                              <span className="text-xs text-gray-400">@ {resourceLabel(ev.FacilityResource)}</span>
                            )}
                          </span>
                          {ev.homeScore !== null && ev.homeScore !== undefined ? (
                            <span className="text-xs font-medium text-gray-700">{ev.homeScore} - {ev.awayScore}</span>
                          ) : (
                            <ScoreEntry event={ev} onSave={(h, a) => saveGameScore(ev.id, h, a, async () => {
                              const runResp = await axios.get(`/api/scheduling/runs/${seasonLastRun.run_id}`, { headers: authHeaders });
                              setSeasonRunDetail(runResp.data);
                            })} />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {activeTab === 'standings' && (
            <div className="bg-white rounded-lg shadow p-4">
              {renderPoolSelector()}
              {standingsLoading ? (
                <div className="text-center py-8 text-gray-500">Loading standings...</div>
              ) : standings.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-8">No standings yet for this pool.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                        <th className="py-2 pr-4">Team</th>
                        <th className="py-2 pr-4">W</th>
                        <th className="py-2 pr-4">L</th>
                        <th className="py-2 pr-4">T</th>
                        <th className="py-2 pr-4">Win%</th>
                        <th className="py-2 pr-4">RS</th>
                        <th className="py-2 pr-4">RA</th>
                        <th className="py-2">Diff</th>
                      </tr>
                    </thead>
                    <tbody>
                      {standings.map((s, i) => (
                        <tr key={s.team_id} className="border-b border-gray-100">
                          <td className="py-2 pr-4 font-medium text-gray-900">{i + 1}. {s.team_name}</td>
                          <td className="py-2 pr-4">{s.wins}</td>
                          <td className="py-2 pr-4">{s.losses}</td>
                          <td className="py-2 pr-4">{s.ties}</td>
                          <td className="py-2 pr-4">{(s.win_pct * 100).toFixed(1)}%</td>
                          <td className="py-2 pr-4">{s.runs_scored}</td>
                          <td className="py-2 pr-4">{s.runs_allowed}</td>
                          <td className="py-2">{s.run_diff > 0 ? `+${s.run_diff}` : s.run_diff}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'postseason' && (
            <div className="bg-white rounded-lg shadow p-4">
              {renderPoolSelector()}

              {!selectedProgramObj || selectedProgramObj.postseason_format === 'none' ? (
                <p className="text-sm text-gray-500 text-center py-8">Set a postseason format on the Programs page first.</p>
              ) : tournamentLoading ? (
                <div className="text-center py-8 text-gray-500">Loading bracket...</div>
              ) : !tournament ? (
                <div className="text-center py-8">
                  <p className="text-sm text-gray-500 mb-3">
                    No bracket yet for this pool ({selectedProgramObj.postseason_format.replace('_', ' ')}).
                  </p>
                  <button
                    onClick={generateTournament}
                    disabled={generatingTournament || !selectedPool}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 mx-auto"
                  >
                    <TrophyIcon className="h-5 w-5" /> {generatingTournament ? 'Generating...' : 'Generate Bracket'}
                  </button>
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-gray-800">
                      {tournament.tournament.format.replace('_', ' ')} — {tournament.tournament.status}
                    </h3>
                    <button
                      onClick={rollbackTournament}
                      className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-red-50 text-red-700 rounded hover:bg-red-100"
                    >
                      <ArrowPathIcon className="h-4 w-4" /> Roll back
                    </button>
                  </div>

                  {['winners', 'losers', 'final'].map(bracketType => {
                    const bracketMatches = tournament.matches.filter(m => m.bracket === bracketType);
                    if (bracketMatches.length === 0) return null;
                    const rounds = Array.from(new Set(bracketMatches.map(m => m.round))).sort((a, b) => a - b);
                    return (
                      <div key={bracketType} className="mb-6">
                        <h4 className="text-xs font-semibold uppercase text-gray-500 mb-2">{bracketType} bracket</h4>
                        <div className="flex gap-4 overflow-x-auto pb-2">
                          {rounds.map(round => (
                            <div key={round} className="flex-shrink-0 w-64 space-y-2">
                              <p className="text-xs font-medium text-gray-500">Round {round}</p>
                              {bracketMatches.filter(m => m.round === round).map(m => (
                                <div key={m.id} className="border border-gray-200 rounded-lg p-2 bg-gray-50">
                                  <div className="text-sm flex items-center justify-between">
                                    <span className={m.WinnerTeam?.id === m.Team1?.id ? 'font-semibold' : ''}>
                                      {m.Team1?.name || (m.team1_seed ? `Seed ${m.team1_seed}` : 'TBD')}
                                    </span>
                                    {m.Event?.homeScore !== null && m.Event?.homeScore !== undefined && (
                                      <span className="text-xs text-gray-600">{m.Event.homeScore}</span>
                                    )}
                                  </div>
                                  <div className="text-sm flex items-center justify-between">
                                    <span className={m.WinnerTeam?.id === m.Team2?.id ? 'font-semibold' : ''}>
                                      {m.Team2?.name || (m.team2_seed ? `Seed ${m.team2_seed}` : 'TBD')}
                                    </span>
                                    {m.Event?.awayScore !== null && m.Event?.awayScore !== undefined && (
                                      <span className="text-xs text-gray-600">{m.Event.awayScore}</span>
                                    )}
                                  </div>
                                  {m.Event?.date && (
                                    <p className="text-xs text-gray-400 mt-1">
                                      {m.Event.date} {m.Event.time?.slice(0, 5)}
                                      {m.Event.FacilityResource?.name && ` @ ${resourceLabel(m.Event.FacilityResource)}`}
                                    </p>
                                  )}
                                  {m.Team1 && m.Team2 && !m.winner_team_id && m.Event && (
                                    <div className="mt-2">
                                      <ScoreEntry event={{ homeScore: m.Event.homeScore, awayScore: m.Event.awayScore }}
                                        onSave={(h, a) => saveMatchScore(m.id, h, a)} />
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ScheduleGenerator;
